// Admin-only card: spawn / list / delete disposable test users so admins can
// experience the brand-new-user flow end-to-end without polluting real signups.
//
// "Sign in as" signs the current admin OUT and into the test user with the
// stored credentials. Closing/reopening the app keeps the test session.
import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, Copy, Trash2, LogIn, RefreshCw, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TestUser {
  user_id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  onboarded: boolean;
  credits: number;
  credits_purchased_total: number;
  tryons_count: number;
}

// Passwords are only returned at creation time. We cache them in localStorage
// so admin can sign in as a previously-spawned test user without re-creating.
const PWD_KEY = "fitaura.admin.test_user_pwds";
const readPwds = (): Record<string, string> => {
  try { return JSON.parse(localStorage.getItem(PWD_KEY) ?? "{}"); } catch { return {}; }
};
const writePwds = (m: Record<string, string>) => {
  try { localStorage.setItem(PWD_KEY, JSON.stringify(m)); } catch { /* ignore */ }
};

async function invoke(action: string, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("admin-test-users", {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.message ?? data.error);
  return data;
}

export function AdminTestUsersCard() {
  const [users, setUsers] = useState<TestUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null); // "create" | userId
  const [pwds, setPwds] = useState<Record<string, string>>(readPwds);
  const [justCreated, setJustCreated] = useState<{ email: string; password: string } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoke("list");
      setUsers((data?.users ?? []) as TestUser[]);
    } catch (e) {
      toast.error("Failed to load test users", { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const createOne = async () => {
    setBusy("create");
    try {
      const data = await invoke("create");
      const u = data?.user as { user_id: string; email: string; password: string };
      const next = { ...pwds, [u.email]: u.password };
      setPwds(next); writePwds(next);
      setJustCreated({ email: u.email, password: u.password });
      toast.success("Test user created", { description: u.email });
      await refresh();
    } catch (e) {
      toast.error("Create failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const deleteOne = async (u: TestUser) => {
    if (!confirm(`Permanently delete ${u.email}?`)) return;
    setBusy(u.user_id);
    try {
      await invoke("delete", { user_id: u.user_id });
      const next = { ...pwds }; delete next[u.email];
      setPwds(next); writePwds(next);
      toast.success("Test user deleted");
      await refresh();
    } catch (e) {
      toast.error("Delete failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const signInAs = async (u: TestUser) => {
    const password = pwds[u.email];
    if (!password) {
      toast.error("No saved password", {
        description: "Password was only shown at creation. Delete and re-create this test user.",
      });
      return;
    }
    setBusy(u.user_id);
    try {
      await supabase.auth.signOut();
      const { error } = await supabase.auth.signInWithPassword({ email: u.email, password });
      if (error) throw error;
      toast.success("Signed in as test user", { description: u.email });
      // Land on root so onboarding/welcome flow kicks in like a new user.
      window.location.assign("/");
    } catch (e) {
      toast.error("Sign-in failed", { description: (e as Error).message });
      setBusy(null);
    }
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-base">Test users</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Spawn throwaway accounts to walk through the new-user flow. Emails end in <code>@fitauratest.dev</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" onClick={() => void createOne()} disabled={busy === "create"}>
            {busy === "create" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UserPlus className="mr-1.5 h-3.5 w-3.5" />}
            New test user
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {justCreated && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs">
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              Save these credentials — password is only shown once
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-background px-2 py-1">{justCreated.email}</code>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copy(justCreated.email, "Email")}>
                <Copy className="h-3 w-3" />
              </Button>
              <code className="rounded bg-background px-2 py-1">{justCreated.password}</code>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copy(justCreated.password, "Password")}>
                <Copy className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto" onClick={() => setJustCreated(null)}>
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No test users yet. Hit "New test user".</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="pb-2 pr-3">Email</th>
                  <th className="pb-2 pr-3">Created</th>
                  <th className="pb-2 pr-3 text-center">Onboarded</th>
                  <th className="pb-2 pr-3 text-right">Credits</th>
                  <th className="pb-2 pr-3 text-right">Try-ons</th>
                  <th className="pb-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const hasPwd = Boolean(pwds[u.email]);
                  return (
                    <tr key={u.user_id} className="border-t">
                      <td className="py-2 pr-3 font-mono">{u.email}</td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {new Date(u.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {u.onboarded ? <Badge variant="secondary">Yes</Badge> : <Badge variant="outline">No</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{u.credits}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{u.tryons_count}</td>
                      <td className="py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            onClick={() => void signInAs(u)}
                            disabled={!hasPwd || busy === u.user_id}
                            title={hasPwd ? "Sign in as this test user" : "Password not saved on this device"}
                          >
                            {busy === u.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogIn className="h-3 w-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-destructive hover:text-destructive"
                            onClick={() => void deleteOne(u)}
                            disabled={busy === u.user_id}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-2 text-[10px] text-muted-foreground">
              Sign-in works only on devices where the password was saved (i.e. the one used to create the user).
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
